import React, { useState, useContext } from 'react';
import {
  Card,
  Typography,
  Row,
  Input,
  Button,
  Popconfirm,
  message,
  Form,
} from 'antd';
import { EditFilled, DeleteFilled } from '@ant-design/icons';
import moment from 'moment';
import API from '../../../api/API';
import { IComment } from '../../../models/comment';
import { CommentLoading } from './index';
import { UserContext } from '../../../contexts';
import './Comment.css';

const { Text } = Typography;
const { TextArea } = Input;

interface IProps {
  comment: IComment;
  fetchComments: () => void;
}

const Comment: React.FC<IProps> = (props: IProps) => {
  // #region Setting up Context, State, Props, etc.

  const { user, isAdmin } = useContext(UserContext);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const { comment, fetchComments } = props;

  // #endregion

  // #region Component Methods

  const onEditClick = () => {
    setEditing(!editing);
  };

  /*
   * When a user finishes editing a comment, this method creates a
   * new edited comment and sends the request to the API and fetches
   * the new comments.
   */
  const onEditFormFinish = async (values: any) => {
    try {
      setSubmitting(true);
      setEditing(false);

      const editedComment = {
        id: comment.id,
        eventId: comment.eventId,
        modifiedBy: user.username,
        modifiedOn: new Date(),
        text: values.text,
        edited: 1,
      };

      await API.put(`/comment/${comment.eventId}`, editedComment);
      await fetchComments();
      setSubmitting(false);
    } catch {
      setSubmitting(false);
      message.error('Looks like something went wrong.');
    }
  };

  /*
   * If a user deletes a comment, we send the associated comment to
   * the API and fetch the new comments.
   */
  const onDeleteClick = async () => {
    try {
      setSubmitting(true);

      const deletedComment = {
        id: comment.id,
        eventId: comment.eventId,
        modifiedBy: user.username,
        modifiedOn: new Date(),
        deleted: 1,
      };

      await API.delete(`/comment/${comment.eventId}`, { data: deletedComment });
      await fetchComments();
      setSubmitting(false);
    } catch {
      setSubmitting(false);
      message.error('Looks like something went wrong.');
    }
  };

  // #endregion

  // #region Render Section

  /*
   * If the user is editing a comment, we display the edit form. If the user
   * is currently submitting changes to a comment, we display the CommentLoading
   * component. Otherwise, we just display the standard comment.
   */
  return !submitting ? (
    <>
      <Card
        className="Comment-card"
        bodyStyle={{ padding: '12px' }}
        style={comment.deleted === '1' ? { backgroundColor: '#f0f0f0' } : {}}
      >
        {!editing ? (
          <>
            <Row>
              <Text strong>{comment.createdBy}</Text>
            </Row>
            <Row>
              <Text className="Comment-card-date-text">
                {comment.edited === '1'
                  ? `${moment(comment.createdOn).fromNow()} (Edited)`
                  : moment(comment.createdOn).fromNow()}
              </Text>
            </Row>
            <Text className="Comment-card-text">{comment.text}</Text>
            {comment.deleted !== '1' && (
              <>
                {/*
                 * Only renders the edit and delete buttons if the currentUser
                 * is an admin, or if they were the one to originally comment.
                 */}
                {(isAdmin || user.username === comment.createdBy) && (
                  <Row justify="end">
                    <button
                      className="Comment-card-button Comment-card-edit-button"
                      onClick={onEditClick}
                      type="button"
                    >
                      <EditFilled className="Comment-card-button-icon" />
                      Edit
                    </button>
                    <Popconfirm
                      title="Are you sure you want to delete this comment?"
                      placement="bottomRight"
                      okText="Yes"
                      onConfirm={onDeleteClick}
                      okButtonProps={{ loading: submitting }}
                    >
                      <button
                        className="Comment-card-button Comment-card-delete-button"
                        type="button"
                      >
                        <DeleteFilled className="Comment-card-button-icon" />
                        Delete
                      </button>
                    </Popconfirm>
                  </Row>
                )}
              </>
            )}
          </>
        ) : (
          <Form form={form} onFinish={onEditFormFinish}>
            <Form.Item
              name="text"
              initialValue={comment.text}
              rules={[
                { required: true, message: 'Comment cannot be empty!' },
                () => ({
                  validator(rule, value) {
                    if (!value || value.trim().length) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(`Comment cannot be empty!`),
                    );
                  },
                }),
              ]}
            >
              <TextArea rows={3} />
            </Form.Item>
            <Row justify="end">
              <Button type="primary" htmlType="submit">
                Edit
              </Button>
            </Row>
          </Form>
        )}
      </Card>
    </>
  ) : (
    <CommentLoading />
  );

  // #endregion
};

export default Comment;
